// Money display — the single place the whole app formats amounts.
//
// Indonesian locale: thousands separator '.', decimal separator ','. The number
// of decimal places is a global setting (Pengaturan → Tampilan → DesimalUang,
// default 0) delivered in the session bootstrap (/api/me, /api/karyawan/me).
//
// `moneyDecimals` is a Solid signal, so every `{formatMoney(x)}` in JSX reads
// it reactively — changing the setting re-renders all amounts with no reload.
//
// DISPLAY ONLY. Parsing the wire string to a Number here never feeds back into
// stored amounts (those stay exact per the money standard); it only renders.
import { createSignal } from 'solid-js';

// Cache the last known value so the first paint doesn't flash a wrong width
// before the session response lands.
const cached = Number(localStorage.getItem('payroll.moneyDecimals'));
const [moneyDecimals, setSig] = createSignal(Number.isInteger(cached) ? cached : 0);
export { moneyDecimals };

// setMoneyDecimals — called from the session bootstrap and from Pengaturan when
// the setting is saved (so the change is visible immediately). Clamped 0..4.
export function setMoneyDecimals(n) {
  const v = Math.max(0, Math.min(4, Math.trunc(Number(n)) || 0));
  try { localStorage.setItem('payroll.moneyDecimals', String(v)); } catch {}
  setSig(v);
}

// toNumber — accept the backend wire string ("8500000.00", "-130000.00"),
// a plain number, or already-grouped text. Returns NaN for non-numeric.
function toNumber(value) {
  if (typeof value === 'number') return value;
  if (value == null || value === '') return NaN;
  return parseFloat(value);
}

// formatMoney — Indonesian currency display rounded to the configured decimals.
// Pass an explicit `decimals` to override the setting for a specific spot.
// Non-numeric input is returned unchanged (e.g. an em dash placeholder).
export function formatMoney(value, decimals) {
  const dp = decimals == null ? moneyDecimals() : decimals;
  const n = toNumber(value);
  if (!Number.isFinite(n)) return value ?? '';
  return n.toLocaleString('id-ID', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

// formatSigned — formatMoney with an explicit leading '+' on positives (for
// deltas where direction matters). Negatives already carry '-'.
export function formatSigned(value, decimals) {
  const n = toNumber(value);
  if (!Number.isFinite(n)) return value ?? '';
  return (n > 0 ? '+' : '') + formatMoney(n, decimals);
}

// formatNumber — grouping with the value's OWN decimals preserved (never forces
// rounding). For master values that may be rates/percentages, not just rupiah,
// so 1,5 stays "1,5" instead of rounding to "2" at the 0-decimal money setting.
export function formatNumber(value, maxDecimals = 4) {
  const n = toNumber(value);
  if (!Number.isFinite(n)) return value ?? '';
  return n.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: maxDecimals });
}

// parseNumericInput — the inverse of formatNumber for user-entered/pasted text.
// Turns a locale-grouped string into the backend engine's canonical grammar
// (optional '-', digits, optional '.' + fraction). Users paste amounts straight
// from Excel, which carry grouping in whatever locale the sheet uses: en-US
// "216,039,234" or id-ID "216.039.234", with decimals as "1,234.5" / "1.234,5".
// The engine rejects every separator, so we strip grouping and normalise the
// decimal mark to '.' here, at the UI boundary that knows this is display text.
//
// Disambiguation: two separator kinds → the LAST is the decimal mark; one kind
// appearing more than once → grouping; a single separator → decimal mark. That
// leaves id-ID rates ("1,5") and engine-form values ("1.5") untouched while
// fixing the multi-group Excel paste that motivated this. A string that isn't
// numeric is returned trimmed and unchanged, so the server still reports its
// own precise error rather than us silently mangling it.
export function parseNumericInput(s) {
  if (s == null) return '';
  const orig = String(s).trim();
  if (orig === '') return '';
  let t = orig;
  const sign = t[0] === '-' ? '-' : '';
  if (t[0] === '-' || t[0] === '+') t = t.slice(1);
  t = t.replace(/\s/g, ''); // \s covers the id-ID U+00A0 grouping space; also drops any space after the sign
  if (!/^[\d.,]*$/.test(t) || !/\d/.test(t)) return orig; // not a plain number

  const lastDot = t.lastIndexOf('.');
  const lastComma = t.lastIndexOf(',');
  let decimalSep = '';
  if (lastDot !== -1 && lastComma !== -1) {
    decimalSep = lastDot > lastComma ? '.' : ',';
  } else if (lastComma !== -1) {
    decimalSep = t.indexOf(',') === lastComma ? ',' : ''; // repeated → grouping
  } else if (lastDot !== -1) {
    decimalSep = t.indexOf('.') === lastDot ? '.' : '';   // repeated → grouping
  }

  let intPart = t, fracPart = '';
  if (decimalSep) {
    const idx = decimalSep === '.' ? lastDot : lastComma; // already computed above
    intPart = t.slice(0, idx);
    fracPart = t.slice(idx + 1); // everything after the LAST separator → provably digit-only
  }
  intPart = intPart.replace(/[.,]/g, '').replace(/^0+(?=\d)/, '');
  if (intPart === '') intPart = '0';
  return sign + intPart + (fracPart ? '.' + fracPart : '');
}
